/* ---------------------------------------------------------------------------
 * diamond-search.js
 *
 * Vanilla custom-elements module for the Purecious custom diamond search
 * (fn-1.3). Wires up filter form ⇄ URL state, infinite-scroll via
 * IntersectionObserver, AbortController-safe fetches, hover/tap video preview,
 * and mobile drawer live-count.
 *
 * Data source: `fetchMockDiamonds` — the single replaceable seam. Task 5 will
 * swap this function body for a real `/apps/diamonds/search` fetch without
 * touching any of the custom elements.
 *
 * IMPORTANT constraints (see task fn-1-*.3 spec):
 *  - No frameworks, vanilla only
 *  - Never edit assets/theme.js — import shared helpers via the "vendor" bare
 *    specifier declared in the theme importmap
 *  - Every fetch MUST abort the previous in-flight fetch
 *  - replaceState on slider drag, pushState on chip clicks / Apply
 * ------------------------------------------------------------------------- */

// We import nothing from "vendor" at the moment — pulling Delegate/inView is
// optional and would force an extra dependency graph. Everything below uses
// platform primitives. If future tasks need delegated events we can opt in.

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const URL_KEYS = /** @type {const} */ ([
  'shape',
  'carat_min',
  'carat_max',
  'color',
  'clarity',
  'cut',
  'price_min',
  'price_max',
  'polish',
  'symmetry',
  'fluorescence',
  'certificate_lab',
  'table_min',
  'table_max',
  'ratio_min',
  'ratio_max',
  'depth_min',
  'depth_max',
  // Fancy color tab — multi-select chips + dual-thumb intensity range.
  // `fancy_color` is comma-joined like the other multi keys. The intensity
  // bounds are single-valued tokens (`Fancy_Deep` .. `Faint`) that the
  // backend translates into an enumerated IN list over FANCY_INTENSITY_ORDER.
  'fancy_color',
  'intensity_min',
  'intensity_max',
  'sort',
  'cursor',
  'limit',
]);

const MULTI_KEYS = new Set([
  'shape',
  'color',
  'clarity',
  'cut',
  'polish',
  'symmetry',
  'fluorescence',
  'certificate_lab',
  'fancy_color',
]);

// Intensity slider stops, left→right. Order must match
// backend/lib/filters.ts FANCY_INTENSITY_ORDER and
// sync/fancy-classifier.js INTENSITY_ORDER.
const FANCY_INTENSITY_TOKENS = [
  'Fancy_Deep',
  'Fancy_Vivid',
  'Fancy_Intense',
  'Fancy_Dark',
  'Fancy',
  'Fancy_Light',
  'Light',
  'Very_Light',
  'Faint',
];

const DEFAULT_LIMIT = 24;
// Default debounce for `input` events (slider drags). Fast enough to feel
// live but long enough to coalesce a drag burst into one fetch.
const DEBOUNCE_MS = 250;
// Longer debounce for number-input typing in the carat/price fields.
// Typical human typing cadence is 300–500ms per keystroke, so a 250ms
// window lapses between characters and fires a fetch mid-word. 500ms waits
// past the end of a typed value like "1500" before committing.
const DEBOUNCE_TYPING_MS = 500;
// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------

/** Debounce `fn` by `ms` milliseconds, trailing edge. */
function debounce(fn, ms) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), ms);
  };
}

/**
 * Serialise a params object to a URLSearchParams, skipping empty values and
 * joining multi-valued keys with a comma as documented in the spec.
 */
function paramsToSearch(params) {
  const sp = new URLSearchParams();
  for (const key of URL_KEYS) {
    const value = params[key];
    if (value == null || value === '') continue;
    if (MULTI_KEYS.has(key)) {
      if (Array.isArray(value) && value.length > 0) {
        sp.set(key, value.join(','));
      }
    } else {
      sp.set(key, String(value));
    }
  }
  return sp;
}

/** Parse current window.location.search into a normalised params object. */
function searchToParams(search) {
  const sp = new URLSearchParams(search);
  const out = {};
  for (const key of URL_KEYS) {
    if (!sp.has(key)) continue;
    const raw = sp.get(key);
    if (MULTI_KEYS.has(key)) {
      out[key] = raw.split(',').filter(Boolean);
    } else {
      out[key] = raw;
    }
  }
  return out;
}

/** Shallow-equal two params objects (arrays compared member-wise). */
function sameParams(a, b) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const av = a[k];
    const bv = b[k];
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length) return false;
      for (let i = 0; i < av.length; i++) if (av[i] !== bv[i]) return false;
    } else if (av !== bv) {
      return false;
    }
  }
  return true;
}

// -----------------------------------------------------------------------------
// Live API fetch — talks to /apps/diamonds/search (Shopify App Proxy) or a
// dev override set via `data-search-endpoint` on <diamond-results>.
// -----------------------------------------------------------------------------

const DEFAULT_ENDPOINT = '/apps/diamonds/search';

/**
 * Resolve the search endpoint from the first <diamond-results> element in the
 * document. Falls back to DEFAULT_ENDPOINT. Looked up lazily on each call so
 * Shopify editor re-renders pick up attribute changes without a reload.
 */
function resolveEndpoint() {
  const el = document.querySelector('diamond-results[data-search-endpoint]');
  const attr = el && el.getAttribute('data-search-endpoint');
  if (attr && attr.trim()) return attr.trim();
  return DEFAULT_ENDPOINT;
}

/**
 * Build a URLSearchParams for the backend. The frontend canonical tokens
 * differ slightly from the API allowlist:
 *   - shape uses underscores (`cushion_modified`) but the API expects a
 *     space (`cushion modified`).
 * `countOnly` maps to the API's `count_only=1` toggle.
 */
function buildApiParams(params, { countOnly } = {}) {
  const sp = new URLSearchParams();
  for (const key of URL_KEYS) {
    const v = params[key];
    if (v == null || v === '') continue;
    if (key === 'shape' && Array.isArray(v) && v.length) {
      sp.set('shape', v.map((s) => s.replace(/_/g, ' ')).join(','));
    } else if (MULTI_KEYS.has(key)) {
      if (Array.isArray(v) && v.length) sp.set(key, v.join(','));
    } else {
      sp.set(key, String(v));
    }
  }
  if (countOnly) sp.set('count_only', '1');
  return sp;
}

/**
 * Normalise an API row (`{ id, shape, carats, color, clarity, cut, price,
 * image, video, lab, report_no }`) to the client-side shape the renderer
 * expects. Shape is lower-cased + space→underscore so it maps to the SVG
 * keys in the local SHAPE_SVGS cache.
 */
function normaliseRow(raw) {
  const shapeKey = String(raw.shape || '')
    .toLowerCase()
    .replace(/\s+/g, '_');
  return {
    id: String(raw.id),
    shape: shapeKey,
    carat: Number(raw.carats),
    color: raw.color || '',
    clarity: raw.clarity || '',
    cut: raw.cut || '',
    price: Number(raw.price),
    certificate_lab: raw.lab || '',
    image: raw.image || '',
    video_url: raw.video || '',
    report_no: raw.report_no || '',
  };
}

/**
 * Structured error the results element can branch on. `status` is the HTTP
 * status code (0 for network failures), `message` is a human-readable string
 * pulled from the response body when available.
 */
class SearchError extends Error {
  constructor(message, { status = 0, kind = 'network' } = {}) {
    super(message);
    this.name = 'SearchError';
    this.status = status;
    this.kind = kind; // 'client' | 'server' | 'network'
  }
}

/**
 * THE SEAM. Fetches from the live `/apps/diamonds/search` endpoint (App Proxy)
 * — or whatever override is configured via `data-search-endpoint`. Callers
 * (DiamondResults, DiamondFiltersDrawer) depend only on the shape
 *   { rows, nextCursor, hasMore, total }
 * and on `signal` support for cancellation. AbortError propagates unchanged.
 *
 * Errors:
 *   4xx → SearchError(kind='client', status, message from response body)
 *   5xx → SearchError(kind='server', status, generic message)
 *   network/parse → SearchError(kind='network', status=0)
 *
 * @param {Object} params normalised filter params
 * @param {Object} opts   { signal?: AbortSignal, countOnly?: boolean }
 */
async function fetchDiamonds(params, opts = {}) {
  const { signal, countOnly } = opts;
  const endpoint = resolveEndpoint();
  const sp = buildApiParams(params, { countOnly });
  const url = sp.toString() ? `${endpoint}?${sp.toString()}` : endpoint;

  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') throw err;
    throw new SearchError('Network error', { status: 0, kind: 'network' });
  }

  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch (_) { /* non-JSON error */ }
    const message =
      (body && (body.message || body.error)) || `Request failed (${response.status})`;
    const kind = response.status >= 500 ? 'server' : 'client';
    throw new SearchError(message, { status: response.status, kind });
  }

  let data;
  try {
    data = await response.json();
  } catch (_) {
    throw new SearchError('Invalid server response', { status: 0, kind: 'network' });
  }

  if (countOnly) {
    const total = Number(data && data.count) || 0;
    return { rows: [], nextCursor: null, hasMore: false, total };
  }

  const rows = Array.isArray(data && data.rows) ? data.rows.map(normaliseRow) : [];
  return {
    rows,
    nextCursor: (data && data.nextCursor) || null,
    hasMore: !!(data && data.hasMore),
    // The search endpoint does not return a running total; callers can hit
    // `count_only=1` separately (the drawer already does this).
    total: rows.length,
  };
}

// Kept as alias so existing call sites + the test hook keep working. Any
// legacy references resolve to the live fetcher.
const fetchMockDiamonds = fetchDiamonds;

// -----------------------------------------------------------------------------
// Card markup helpers (used when appending infinite-scroll pages)
// -----------------------------------------------------------------------------

// Title-case labels used for card alt text and the small shape chip below
// each thumbnail. Keep in sync with locales/en.default.json. When the row's
// normalized shape key isn't in this table we fall through to the raw key
// (e.g. "square_radiant"), which is ugly but non-broken.
const SHAPE_LABELS = {
  // Primary 12
  round: 'Round', princess: 'Princess', cushion: 'Cushion',
  oval: 'Oval', pear: 'Pear', emerald: 'Emerald',
  heart: 'Heart', radiant: 'Radiant', asscher: 'Asscher',
  marquise: 'Marquise',
  square_radiant: 'Square Radiant', square_emerald: 'Square Emerald',
  // Secondary 22
  old_miner: 'Old Miner', rose: 'Rose', square: 'Square',
  half_moon: 'Half Moon', trapezoid: 'Trapezoid', flanders: 'Flanders',
  briolette: 'Briolette', pentagonal: 'Pentagonal',
  hexagonal: 'Hexagonal', octagonal: 'Octagonal', triangular: 'Triangular',
  trilliant: 'Trilliant', calf: 'Calf',
  tapered_baguette: 'Tapered Baguette', shield: 'Shield', lozenge: 'Lozenge',
  kite: 'Kite', european_cut: 'European Cut', baguette: 'Baguette',
  bullet: 'Bullet', tapered_bullet: 'Tapered Bullet', other: 'Other',
  // Back-compat: the DB still stores CUSHION MODIFIED / CUSHION BRILLIANT
  // as distinct values. The UI collapses them under the single `cushion`
  // chip, but rows that carry the variant token still need a readable
  // label.
  cushion_modified: 'Cushion', cushion_brilliant: 'Cushion',
};

// Inline SVGs mirroring snippets/diamond-shape-icon.liquid. Used as the image
// fallback when a row has no `image` URL — we never emit `<img src="">` for
// missing images since that would trigger a 404. These are the multi-colour
// Nivoda-style icons; keep in sync with the Liquid snippet and see the
// 2026-04-08 entry in theme/CUSTOMIZATIONS.md for the rationale.
const CUSHION_SVG = '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M14.037 1.022c-1.426 0-7.546 0-10.26 3.263-2.712 3.262-2.776 9.463-2.776 9.463s-.12 6.49 2.777 9.946c2.898 3.458 8.685 3.282 10.167 3.282 1.48 0 7.6 0 10.36-3.29 2.76-3.292 2.695-5.896 2.695-9.9s-.176-6.489-2.778-9.585-7.88-3.179-10.185-3.179"/><path fill="#E4E4E4" d="M16.528 7.084H11.66l2.3-3.2zM7.829 16.956v-6.04l-3.29 3.065zM16.303 20.877H11.66l2.3 3.335zM20.18 10.916v6.04l3.29-2.975z"/><path fill="#E4E4E4" d="M8.48 1.555c1.683 1.684 6.296 4.309 10.96.267zM26.53 19.434l-.203-10.695-2.865 5.251zM19.36 26.425c-1.943-1.842-6.84-4.378-10.88.216zM4.531 13.99 1.806 8.32l-.891 5.202.891 5.65z"/><path stroke="#80838D" stroke-width="0.4" d="M7.804 14V10.85l-.669-3.78H13.4 7.135l.67 3.78z" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="M13.986 3.865c-1.604.03-2.676-.345-3.369-.706 0 0-.17-.089-1.85-1.11L7.135 7.07 2.181 9.3l2.374 4.7s7.02-6.253 9.431-10.134ZM20.195 14V10.85l.67-3.78h-6.407 6.406l-.669 3.78z" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="M14.014 3.865c1.604.03 2.676-.345 3.369-.706 0 0 .17-.089 1.85-1.11l1.631 5.021L26.01 9.3l-2.565 4.7s-7.02-6.253-9.431-10.134Z" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="M14.014 20.93h6.85l-.669-3.781V14"/><path stroke="#80838D" stroke-width="0.4" d="M14.014 20.93h6.85l-.669-3.781V14"/><path stroke="#80838D" stroke-width="0.4" d="M14.014 24.135c1.604-.03 2.676.345 3.369.706 0 0 .17.089 1.85 1.11l1.631-5.021L26.01 18.7l-2.565-4.7s-7.02 6.253-9.431 10.134Z" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="M13.986 20.93h-6.85l.668-3.781V14"/><path stroke="#80838D" stroke-width="0.4" d="M14.458 20.93H7.135l.67-3.781V14"/><path stroke="#80838D" stroke-width="0.4" d="M13.986 24.135c-1.604-.03-2.676.345-3.369.706 0 0-.17.089-1.85 1.11L7.135 20.93 2.181 18.7l2.374-4.7s7.02 6.253 9.431 10.134Z" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="M14.458 7.07H13.4"/><path stroke="#1C2024" stroke-width="1.4" d="M14.037 1.022c-1.426 0-7.546 0-10.26 3.263-2.712 3.262-2.776 9.463-2.776 9.463s-.12 6.49 2.777 9.946c2.898 3.458 8.685 3.282 10.167 3.282 1.48 0 7.6 0 10.36-3.29 2.76-3.292 2.695-5.896 2.695-9.9s-.176-6.489-2.778-9.585-7.88-3.179-10.185-3.179Z"/><path fill="#E4E4E4" d="m8.823 11.707 3.175-2.975 5.98 9.126-2.031 2.118h-3.948l-3.176-3.462z"/></svg>';
const SHAPE_SVGS = {
  round: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M14 27c7.18 0 13-5.82 13-13S21.18 1 14 1 1 6.822 1 14s5.82 13 13 13"/><path fill="#E4E4E4" d="M14 20.5c-1 .5-3.1 1.6-3.5 2L14 27l3.5-5zM17.5 6 14 1l-3.5 5L14 7.5zM6 10.5 1 14l5 3.5L7.5 14zM20.5 14l1.5 3.5 5-3.5-5-3.5zM6 17.5 2 19l3 4z"/><path fill="#E4E4E4" d="m9 26 2-4-6 1zM10.5 6 5 5l1 6-4-2 3-4 4-3zM17 6l2-4 4 3 3 4-4 1.5L23 5zM23 23l-5.5-1 1.5 4zM22 17l4 2-3 4z"/><path stroke="#80838D" stroke-width="0.4" d="m10.563 5.825 3.363-4.72 3.418 4.683m-6.781.037L4.83 4.924l.957 5.722L2.09 9.141m8.473-3.316L9.439 9.429l-1.896 4.645-1.718 3.354m4.738-11.603 3.353 1.719 4.655 1.904 3.595 1.115M10.563 5.825 9.01 2.137m8.334 3.651L14.01 7.544l-4.635 1.95-3.586 1.152m11.555-4.858 1.163 3.595 1.941 4.626 1.756 3.335m-4.86-11.556 1.524-3.726M5.825 17.428l3.604 1.124 4.654 1.895 3.354 1.728M5.825 17.428l-.892 5.741 5.723-.966m-4.831-4.775-4.72-3.363 4.684-3.419m.036 6.782-3.688 1.56m20.029-8.425.9-5.732-5.723.956m4.823 4.776 4.728 3.372-4.691 3.41m-.037-6.782-1.719 3.353-1.895 4.655-1.115 3.604M5.789 10.646l1.754 3.335 1.952 4.636 1.161 3.586m6.781-.028-3.372 4.72-3.409-4.692m6.781-.028 5.732.892-.966-5.722m-4.766 4.83 1.552 3.697m-8.333-3.669 3.335-1.756 4.626-1.942 3.586-1.16m-11.547 4.858-1.533 3.744M25.872 9.011l-3.697 1.552m3.762 8.305-3.734-1.514m-3.632 1.17 1.857-4.561-1.904-4.534-4.561-1.867-4.534 1.905-1.867 4.56 1.914 4.544 4.561 1.858z"/><path stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M14 27c7.18 0 13-5.82 13-13S21.18 1 14 1 1 6.822 1 14s5.82 13 13 13Z"/><path fill="#E4E4E4" d="m9 13.85 1.342-3.663 7.158 7.327-3.579 1.374-3.579-1.374z"/></svg>',
  oval: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M4.284 14.185C4.284 6.051 8.635 1 14 1c5.363 0 9.716 5.042 9.716 13.176S19.363 27 14 27c-5.365 0-9.716-4.643-9.716-12.815"/><path fill="#E4E4E4" d="M8.962 14.18 7.02 16.6l3.153 2.675zM14.021 21.768l-3.336 1.283-.45-3.47zM18.127 19.276 14.26 21.86l3.06 1.19zM21.096 16.6l-1.98-2.42-.696 5.096zM21.096 11.467 17.54 9.194l1.577 4.73zM17.32 5.015l-3.299 1.687 3.404 2.31zM13.849 6.901l-3.164-1.886-.22 4.18zM10.172 9.524l-3.153 1.943 1.943 2.456zM13.637 1.466 8.65 2.941l2.107 2.107zM18.589 2.766l-4.18-1.3 2.845 3.582 1.686-1.756zM21.082 11.37l1.054-4.18.983 3.618z"/><path fill="#E4E4E4" d="m17.254 22.889 1.335 2.599 2.95-2.95zM10.757 22.889l-4.179-.562 2.072 3.477zM7 16.708l-2.143.737 1.16 4.355zM7 11.37l-.984-4.53-1.159 3.687z"/><path stroke="#80838D" stroke-width="0.4" d="m23.554 18.365-2.465-1.756-1.9-2.57-1.006 5.367-4.148 2.433 3.253 1.15m3.802-6.379-2.909 2.795-.892 3.584 1.716 2.526m-1.717-2.526L14.035 27m3.253-4.011 4.662-.52-.86-5.86 2.9-2.359v-.138l-2.9-2.723 1.239-4.864-5.041-1.505m0 0L14.035 1m3.253 4.02.308 4.16m-.308-4.16-3.253 1.812 3.561 2.348m-.308-4.16 2.153-2.107M17.596 9.18l3.494 2.211-1.9 2.72zm6.06 1.319-2.565.891M4.355 17.938l2.613-1.328m0 0 3.126 2.73m-3.126-2.73 1.9-2.572 1.226 5.302m-3.126-2.73-1.01 5.655 4.813.724m-.677-3.649 3.929 2.497-3.252 1.152m0 0-.676-3.649m.676 3.649-1.936 2.423m1.936-2.423L14.023 27M6.968 16.609l-2.9-2.358v-.138l2.9-2.723L5.73 6.406l5.035-1.385L14.023 1m-9.634 9.229 2.577 1.162m0 0L10.46 9.18m-3.494 2.211 1.901 2.719 1.593-4.931m0 0 .31-4.16m-.31 4.16 3.563-2.348-3.253-1.812m0 0L8.604 2.876M4 14.185C4 6.051 8.479 1 14 1c5.52 0 10 5.042 10 13.176S19.52 27 14 27C8.479 27 4 22.357 4 14.185Z"/><path stroke="#1C2024" stroke-width="1.4" d="M4.284 14.185C4.284 6.051 8.635 1 14 1c5.363 0 9.716 5.042 9.716 13.176S19.363 27 14 27c-5.365 0-9.716-4.643-9.716-12.815Z"/><path fill="#E4E4E4" d="m11.027 18.411-.886-4.286 1.238-3.993 5.813 8.279-3.033 1.881z"/></svg>',
  pear: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M13.981 27s10.02-5.674 10.02-16.873C24 1.327 14.626 1 13.98 1 13.336 1 4 1.066 4 10.127 3.96 21.334 13.98 27 13.98 27"/><path fill="#E4E4E4" d="m16.735 5.038-2.651 1.476 3.193 1.657zM20.682 9.226l-3.177-.845 1.158 3.526zM20.682 15.01l-1.808-2.922-1.37 5.333zM10.74 17.421l3.223 2.32-2.591 1.296zM9.293 12.299 7.274 15.01l3.224 2.109zM7.124 9.226l2.169 2.681 1.205-3.736zM11.07 5.038l-.291 2.868 3.033-1.392zM17.374 17.684l-3.224 1.96 2.248 1.48zM11.557 21.168l-1.492 2.377 3.99 3.265zM16.634 21.168l-2.579 5.037 3.708-1.854zM20.745 15.042l.282 4.715 1.975-3.426zM20.745 9.36l2.982 2.337.242-3.385zM16.836 5.047l4.796-.644-3.386-2.459zM11.154 5.047 9.179 1.944 5.914 4.403zM7.083 9.36 4.182 8.312v3.385z"/><path fill="#E4E4E4" d="m7.446 15.042-2.58 1.531 2.217 3.587z"/><path stroke="#80838D" stroke-width="0.4" d="M11.104 5.02 6.01 4.289l1.145 4.956m0 0-3.08 2.535 3.172 3.212-.414 4.93 4.65 1.272M7.154 9.245l3.413-1.104M7.155 9.245 4.193 8.04m6.375.101.536-3.119 2.84-3.937m-3.376 7.056L9.35 12.189l1.246 5.189m-.028-9.237 3.377-1.616m-6.79 2.72 2.195 2.943-2.103 2.805m4.235 6.201 2.537-1.466-3.423-2.35m.886 3.816-.886-3.816m0 0-3.35-2.386-2.24 1.356m8.94-9.823L11.102 5.02 9.524 2.004m4.421 4.521 3.385 1.616m-3.385-1.616 2.85-1.504M9.996 23.843l1.486-2.647 2.537 5.627M17.33 8.141l1.338 3.797M17.33 8.141l3.412 1.103 3.138 2.536-3.165 3.214-3.2 2.384-3.57 2.35 2.537 1.467m.848-13.054-.537-3.12L13.944 1m4.724 10.938 2.048 3.055m-2.048-3.055-1.152 5.44-1.034 3.817m.313-16.174 1.513-3.036m-1.513 3.036 5.065-.706-1.116 4.93m0 0-2.076 2.694m2.076-2.694 3.026-.853m-3.054 6.601.36 5.014-4.594 1.188m4.234-6.202 2.28 1.253m-6.514 4.949 1.227 2.896m-1.227-2.896-2.537 5.627m.036.178s10.02-5.674 10.02-16.873C24 1.327 14.626 1 13.98 1 13.336 1 4 1.066 4 10.127 3.96 21.334 13.98 27 13.98 27Z"/><path stroke="#1C2024" stroke-linejoin="round" stroke-width="1.4" d="M13.981 27s10.02-5.674 10.02-16.873C24 1.327 14.626 1 13.98 1 13.336 1 4 1.066 4 10.127 3.96 21.334 13.98 27 13.98 27Z"/><path fill="#E4E4E4" d="m10.758 12.44 1.093-3.38 4.703 7.406-2.547 1.763-2.156-1.42z"/></svg>',
  emerald: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M6.685 27.662H21.77l2.715-3.48V4.458L21.77.978H6.685L3.96 4.458v19.725z"/><path fill="#E4E4E4" d="M8 3.5 6 6l2.117 1.51 1.024-1.365zM20.5 3.5l-1.336 2.74 1.13 1.24 2.136-1.43zM9.191 22.439 8.113 21.03l-2.146 1.65L8 25zM20.27 21.142l-1.043 1.326 1.2 2.526 1.926-2.4z"/><path fill="#E4E4E4" d="M21.655.9H6.905l1.143 2.837h12.44zM6.178 6.097 3.78 4.64l.152 19.366 2.064-1.427zM24.598 4.64l-2.305 1.457V22.58l2.305 1.427zM20.403 25.026H7.968l-1.247 2.57h14.967z"/><path stroke="#80838D" stroke-width="0.4" d="m24.222 4.71-3.928 2.767M20.294 7.477l-1.027-1.273M21.576 1.32l-2.309 4.884M19.267 6.204H9.186M6.878 1.32l2.308 4.884M24.222 23.93l-3.927-2.776M21.576 27.32l-2.309-4.884M9.186 6.204 8.16 7.477M4.223 4.71 8.16 7.476M8.16 7.477v3.435M4.222 23.93l2.656 3.39M4.222 23.93l3.937-2.776M6.878 27.32l2.308-4.885-1.027-1.28"/><path stroke="#80838D" stroke-width="0.4" d="M8.16 10.913v10.241l1.025 1.282h10.083l1.027-1.282V7.477"/><path stroke="#80838D" stroke-width="0.4" d="M22.41 11.11v11.355l-1.988 2.52H8.03l-1.995-2.52V6.166l1.995-2.52h12.393l1.988 2.52v4.943"/><path fill="#E4E4E4" d="m9.785 21.553-.599-.76V7.737l.509-.666 9.683 13.449-.783 1.033z"/><path stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M6.685 27.662H21.77l2.715-3.48V4.458L21.77.978H6.685L3.96 4.458v19.725z"/></svg>',
  radiant: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" fill-rule="evenodd" stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M5.261 13.854v10.46l2.361 2.74h12.765l2.36-2.74V3.394l-2.36-2.74H7.622l-2.36 2.74z" clip-rule="evenodd"/><path fill="#E4E4E4" fill-rule="evenodd" d="m7.777.738 1.714 2.398h9.01L20.387.738zm12.61 5.105 1.952-2.467v20.729l-1.953-2.124zm-12.884 0L5.105 2.965l.48 21.585 1.918-2.57zm.248 20.952 1.812-2.245h8.854l1.97 2.245zm5.732-20.938c-1.104-.54-2.972-1.52-3.795-2.471v2.648c.53.117 2.227.276 3.795-.177M9.688 24.391c.07-.824 2.716-2.167 4.03-2.736-1.692-.735-3.429-.475-4.201-.22zm8.766-2.956v2.956c-.259-.66-2.932-2.099-4.236-2.736.889-.575 2.991-.46 4.236-.22M10.984 7.56v12.255c.858-.3 1.843-.433 3.143.214 1.477-.76 2.114-.67 3.515-.214zm7.382-1.35V3.386c-1.112 1.203-3.226 2.083-4.246 2.408.746.703 3.142.57 4.246.416" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="m7.818 1.059 1.947 2.444s3.66 4.281 10.74 2.226"/><path stroke="#80838D" stroke-width="0.4" d="m20.192 1.059-1.948 2.444s-4.256 4.24-10.74 2.226M7.818 26.65l1.947-2.445s3.958-4.568 10.74-2.226M5.496 24.475l2.007-2.496V5.729L5.496 3.233"/><path stroke="#80838D" stroke-width="0.4" d="m20.192 26.65-1.948-2.445s-3.963-4.608-10.74-2.226M22.514 24.475l-2.008-2.496V5.729l2.008-2.496"/><path stroke="#80838D" stroke-width="0.4" d="M9.537 24.597V3.111h8.936v21.486H9.537Z" clip-rule="evenodd"/><path stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M5.261 13.854v10.46l2.361 2.74h12.765l2.36-2.74V3.394l-2.36-2.74H7.622l-2.36 2.74z" clip-rule="evenodd"/></svg>',
  princess: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M26.5 1.5h-25v25h25z"/><path fill="#E4E4E4" d="m18.5 18.5-9-9L8 14l1.5 4.5L14 20zM19.5 4.5l-5 2 5 2zM13.5 6.5l-5-2v4zM23.5 8.5h-4l2 5.5zM21.5 14l-2 5.5h4zM19.5 19.5l-5.5 2 5.5 2zM14 21.5l-5.5-2v4zM8.5 19.5h-4l2-5.5zM8.5 8.5h-4l2 5.5zM25 2H3l5.5 2.5h11zM19.5 23.5h-11l-6.338 3H26zM26 2.5l-2.5 6v11l3 6.5zM4.5 8.5 2 3l-.5 23 3-6.5z"/><path stroke="#80838D" stroke-width="0.4" d="m26.5 1.5-7 3m0 0-11 4v-4m11 0h-11m11 0v4l-11-4m0 0-7-3M26.5 26.5l-3-7-4-11h4v11h-4l4-11 3-7"/><path stroke="#80838D" stroke-width="0.4" d="m1.5 26.5 7-3 11-4v4h-11v-4l11 4 7 3"/><path stroke="#80838D" stroke-width="0.4" d="m1.5 1.5 3 7 4 11h-4v-11h4l-4 11-3 7"/><path stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M26.5 1.5h-25v25h25z"/></svg>',
  marquise: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M14.08 27.234c2.585-1.264 4.749-3.179 6.249-5.533a14.54 14.54 0 0 0 2.304-7.85C22.633 4.791 14.08.765 14.08.765h-.161S5.367 4.792 5.367 13.849A14.55 14.55 0 0 0 7.673 21.7c1.5 2.354 3.664 4.27 6.248 5.533z"/><path fill="#E4E4E4" d="m17.435 3.612-2.01 2.01 4.982 1.442zM12.452 5.622 9.874 2.956 7.426 7.064zM8.562 17.291l-2.928-3.89.219 3.89 2.01 4.37zM22.024 13.926l-2.666 3.365.874 4.37 1.791-2.403zM15.424 22.317h-2.972l1.622 5.153zM19.916 21.025l-4.087 1.292 1.845 2.023 2.242-2.023zM12.142 22.317l-3.888-1.292-.15.4L9.95 24.34l2.193-1.695zM15.424 5.417l-2.703.205-.142-.347 1.473-4.3zM19.506 10.708 16.34 8.263l1.684 5.651zM11.81 8.263l-3.367 2.445 1.603 3.206zM10.046 14.476l1.564 4.57-3.167-2.005zM19.346 17.041l-3.007 2.365 1.684-4.93z"/><path fill="#E4E4E4" d="m8.519 10.736-.437-3.54-2.491 2.971v4.021zM22.242 10.167l-2.229-3.409-.612 3.978 2.841 3.19z"/><path stroke="#80838D" stroke-width="0.4" d="m17.694 3.486-2.2 2.118m0 0 .78 2.737 3.262 2.407-1.565 3.109m-2.477-8.253-1.42-4.475h-.147l-1.42 4.474m2.987 0 4.408 1.327-.365 3.818 2.454 3.109-2.576 3.315M15.494 5.604l-2.987-.001m5.464 8.254 1.444 3.315m-1.444-3.315-1.573 5.461 3.017-2.146m-1.444-3.315L16.277 8.34h-4.554m.784-2.737L8.097 6.93l.367 3.819m0 0 3.26-2.41m-3.26 2.41L6.01 13.857l2.585 3.314-.57 3.855 4.515 1.299M8.464 10.749l1.572 3.11-1.444 3.315 3.01 2.144m.903-13.712-.782 2.734m0 0-1.687 5.517 1.566 5.462.936 3.007-2.342 2.118m5.264-2.118.937-3.007m-.937 3.007 2.342 2.118m-2.342-2.118 4.518-1.3-.562-3.854m-3.956 5.154-1.384 4.546h-.149l-1.387-4.546zM10.313 3.485l2.19 2.119"/><path stroke="#1C2024" stroke-linejoin="round" stroke-width="1.4" d="M14.08 27.234c2.585-1.264 4.749-3.179 6.249-5.533a14.54 14.54 0 0 0 2.304-7.85C22.633 4.791 14.08.765 14.08.765h-.161S5.367 4.792 5.367 13.849A14.55 14.55 0 0 0 7.673 21.7c1.5 2.354 3.664 4.27 6.248 5.533z"/><path stroke="#80838D" stroke-width="0.4" d="M11.601 19.318h4.797"/><path fill="#E4E4E4" d="m11.119 13.895 1.464-4.757 3.781 6.984-.793 2.073H12.37z"/></svg>',
  cushion: CUSHION_SVG,
  // Back-compat: deep links may still carry the DB variant tokens.
  cushion_modified: CUSHION_SVG,
  cushion_brilliant: CUSHION_SVG,
  square_radiant: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M1.5 22.527 5.48 26.5h17.047l3.973-3.973V5.48L22.527 1.5H5.48L1.5 5.48z"/><path fill="#E4E4E4" d="M6.902 4.2 5.561 1.631h16.862l-1.341 2.567zM26.294 5.656 23.61 6.92v14.141l2.683 1.419zM21.082 23.668H6.902l-1.341 2.759h16.862zM4.22 21.062 1.537 22.48V5.502L4.22 6.92zM8.254 19.788l-3.819 1.167 2.97-7.001zM7.26 13.804l.994-5.437-3.819-1.171zM13.946 7.413 7.086 4.3l1.38 4.066z"/><path fill="#E4E4E4" d="M14.335 7.413 21.053 4.3l-1.296 3.726 3.983-1.041-2.97 6.968-1.013-5.927-.118.34zM20.638 14.253l3.102 6.702-3.985-1.134 1.298 3.927-7.107-3.04 5.809-.887-.116-.033zM13.487 20.707l-5.021-.92-1.662 3.96z"/><path stroke="#80838D" stroke-width="0.4" d="m23.804 21.071-4.027-1.311 1.294 4.044m2.733-2.733V6.94m0 14.132-2.733 2.733m2.733-2.733 2.696 1.456m-2.696-1.456L20.768 14l3.036-7.06M21.07 23.803l1.456 2.696m-1.456-2.696L14 20.695l-7.062 3.109m14.133 0H6.938M23.804 6.939 26.5 5.481m-2.696 1.458L21.07 4.203H6.938m16.866 2.736L19.777 8.25l1.294-4.044M6.938 23.804 5.481 26.5m1.457-2.696L4.202 21.07V6.94l2.736-2.736m0 19.6 1.293-4.044-4.026 1.311M6.938 4.203 5.481 1.5m1.457 2.703L14 7.313l7.072-3.108L22.527 1.5M4.205 21.071 1.5 22.527m2.705-1.456L7.23 14 4.205 6.938m0 0L1.5 5.481m2.705 1.457L8.23 8.25 6.938 4.206M14 7.375l5.696.947.875 5.678-.875 5.688-5.696.947-5.696-.948L7.438 14l.866-5.678z"/><path stroke="#1C2024" stroke-width="1.4" d="M1.5 22.527 5.48 26.5h17.047l3.973-3.973V5.48L22.527 1.5H5.48L1.5 5.48z"/><path fill="#E4E4E4" d="m9.208 13.77.566-4.47 8.83 8.996-4.643.962-4.187-.962z"/></svg>',
  square_emerald: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M4.476 26.348h19.059l2.715-3.479V5.131l-2.715-3.48H4.475L1.75 5.132v17.738z"/><path fill="#E4E4E4" d="m5.783 23.7-1.907-2.27 1.907-1.589 1.044 1.59zM6.827 6.811 5.783 7.946 3.876 6.811l1.907-2.497zM22.309 4.314l-1.271 2.497 1.27 1.135 1.908-1.135zM22.309 19.841l-1.271 1.317 1.27 2.542 1.908-2.542zM23.386 26.107H4.68l.99-2.276h16.692zM23.386 1.794H4.68l.99 2.441h16.692z"/><path fill="#E4E4E4" d="M3.89 6.643 1.58 5.16l.297 17.55 2.012-1.485zM24.177 6.643l2.277-1.32V22.71l-2.277-1.32z"/><path stroke="#80838D" stroke-width="0.4" d="m25.987 5.384-3.766 2.649M23.341 1.994l-2.167 4.74M4.669 1.994l2.166 4.74M25.987 22.616l-3.765-2.658M23.341 26.006l-2.166-4.741M2.014 5.383l3.774 2.65M2.013 22.617l2.656 3.39M2.013 22.616l3.775-2.658M4.669 26.007l2.165-4.741M24.18 11.699v9.596l-2.011 2.55H5.839l-2.02-2.55V6.696l2.02-2.55h16.33l2.012 2.55v5.002"/><path stroke="#80838D" stroke-width="0.4" d="M5.788 11.538v8.42l1.046 1.308 14.34-.001 1.048-1.307V8.033l-1.048-1.299H6.835L5.788 8.033v3.504"/><path fill="#E4E4E4" d="m7.196 20.463-.497-.584V8.577l.497-.74 14.302 11.689-.678.937z"/><path stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M4.476 26.348h19.059l2.715-3.479V5.131l-2.715-3.48H4.475L1.75 5.132v17.738z"/></svg>',
  heart: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" d="M13.986 25.72S27 20.394 27 8.962c0-2.923-2.324-6.681-6.19-6.681-4.909 0-6.824 2.886-6.824 2.886S12.126 2.28 7.191 2.28C3.333 2.28 1 6.039 1 8.96 1 20.395 14.014 25.72 14.014 25.72z"/><path fill="#E4E4E4" d="m8.707 12.74 1.508-2.982 7.268 4.73-3.428 3.669zM13.667 19.57l-6.383-6.794.214 4.497zM20.714 17.273v-4.497l-6.427 6.794zM9.637 8.17 6.62 6.744l-1.865 4.661 2.528 1.163zM21.372 6.744l-2.953 1.37 2.02 4.662 2.877-1.58z"/><path fill="#E4E4E4" d="M9.873 2.637 4.81 3.12l1.886 3.751zM7.562 21.784l-.333-4.537-4.059-1.462zM4.815 11.336 1.61 6.882l-.416 2.956 1.54 5.91zM9.644 8.006l4.35-2.48 4.432 2.48-4.329 2.04zM22.88 2.637h-4.454l2.914 4.245zM26.293 6.674l-3.163 4.662 2.081 4.204 1.707-3.954zM20.674 17.247v4.537l4.162-5.999zM13.843 19.793l-2.127 4.583 2.457 1.019 1.917-1.288z"/><path stroke="#80838D" stroke-width="0.4" d="m4.73 11.278 2.588 1.456v8.895M4.729 11.278 2.9 15.533 14 19.777m-9.27-8.499-2.775-4.23 4.74-.3m-1.966 4.53 1.967-4.53m3.125-4.16-3.125 4.16m0 0-2.07-4.284"/><path stroke="#80838D" stroke-width="0.4" d="M20.654 21.49v-8.756m0 0 2.589-1.456m-2.589 1.456L14 19.777m6.654-7.043-1.251-2.379-1.252-2.378-4.183-2.5m9.275 5.801 1.83 4.255L14 19.777m9.243-8.499 2.774-4.23-4.74-.3m1.966 4.53-1.967-4.53m0 0-3.125-4.16m3.125 4.16-7.29 3.298-7.29-3.298m14.58 0 2.07-4.284M14 19.777l-6.682-7.043L9.82 7.977l4.183-2.5M7.318 21.63 14 19.776m0 0-2.007 4.281M14 19.778l2.055 4.28M20.654 21.49 14 19.776"/><path stroke="#1C2024" stroke-linejoin="round" stroke-width="1.4" d="M13.986 25.72S27 20.394 27 8.962c0-2.923-2.324-6.681-6.19-6.681-4.909 0-6.824 2.886-6.824 2.886S12.126 2.28 7.191 2.28C3.333 2.28 1 6.039 1 8.96 1 20.395 14.014 25.72 14.014 25.72z"/></svg>',
  asscher: '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" fill-rule="evenodd" d="M21.5 26.5h-15l-5-5v-15l5-5h15l5 5v15z" clip-rule="evenodd"/><path fill="#E4E4E4" d="m7.63 4.109.96 2.481-2.001 2.082L4.067 7.67v12.728l2.522-1.041 2 1.961-.96 2.642h12.688l-1.04-2.642 2.24-1.961 2.402 1.04V7.672l-2.401 1-2.242-2.08 1.041-2.482L23.92 7.67l2.325-1.06-.087 14.708-2.238-.92-3.602 3.562 1.056 2.136-14.775.078 1.031-2.214-3.562-3.562-2.25 1.044L1.146 6.31l2.921 1.36zM21.518 1.467H6.588L7.63 3.968l12.893.14z"/><path stroke="#80838D" stroke-width="0.4" d="m7.64 24.01-3.65-3.65V7.64l3.65-3.65h12.72l3.65 3.65v12.72l-3.65 3.65z" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="m8.62 21.36-1.98-1.98V8.62l1.98-1.979 10.76-.001 1.98 1.98v10.76l-1.98 1.98z" clip-rule="evenodd"/><path stroke="#80838D" stroke-width="0.4" d="m6.5 1.5 1.06 2.57 1.06 2.57M1.5 6.5l2.57 1.06 2.57 1.06M26.5 6.5l-2.57 1.06-2.57 1.06M21.5 1.5l-1.06 2.57-1.06 2.57M21.5 26.5l-1.06-2.57-1.06-2.57M26.5 21.5l-2.57-1.06-2.57-1.06M1.5 21.5l2.57-1.06 2.57-1.06M6.5 26.5l1.06-2.57 1.06-2.57"/><path stroke="#1C2024" stroke-miterlimit="10" stroke-width="1.4" d="M21.5 26.5h-15l-5-5v-15l5-5h15l5 5v15z" clip-rule="evenodd"/><path fill="#E4E4E4" d="m7.639 9.186 1.454-1.377 11.402 11.108-1.428 1.493H8.94l-1.302-1.493z"/></svg>',
};
const FALLBACK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" class="diamond-shape-icon" width="96" height="96" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path fill="#fff" stroke="#1C2024" stroke-width="1.4" d="M14 2l11 12-11 12L3 14z"/></svg>';

function shapeSvg(shapeKey) {
  return SHAPE_SVGS[shapeKey] || FALLBACK_SVG;
}

function formatPrice(n) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(n);
  } catch (_) {
    return `$${Math.round(n)}`;
  }
}

/** Escape text for safe interpolation into HTML attributes / text nodes. */
function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build a <diamond-card> DOM node for a given row. Mirrors the structure
 * produced by snippets/diamond-card.liquid so the JS-rendered cards get all
 * the same CSS from the theme.
 *
 * Image fallback: when `row.image` is null/empty we inline the shape SVG
 * directly into the `.diamond-card__shape` box (no `<img src>` to 404 on).
 */
function buildCardElement(row) {
  const href = `/pages/diamond?id=${encodeURIComponent(row.id)}`;
  const shapeLabel = SHAPE_LABELS[row.shape] || row.shape;
  const caratText = Number.isFinite(row.carat) ? row.carat.toFixed(2) : String(row.carat);

  const card = document.createElement('diamond-card');
  card.className = 'diamond-card';
  card.dataset.diamondId = row.id;
  if (row.video_url) card.dataset.videoUrl = row.video_url;

  const mediaInner = row.image
    ? `<img class="diamond-card__image" src="${esc(row.image)}" alt="" loading="lazy" decoding="async" style="width:100%;height:100%;object-fit:cover">`
    : shapeSvg(row.shape);

  const cutDisplay = row.cut === '-' || !row.cut ? 'Ungraded' : row.cut;

  // Build a descriptive aria-label so screen readers announce the full
  // spec ("1.05 carat round diamond, color D, clarity VVS2, $1,850") rather
  // than just "link".
  const ariaParts = [
    `${caratText} carat ${shapeLabel} diamond`,
    row.color ? `color ${row.color}` : '',
    row.clarity ? `clarity ${row.clarity}` : '',
    (row.cut && row.cut !== '-') ? `cut ${row.cut}` : '',
    formatPrice(row.price),
  ].filter(Boolean);
  const ariaLabel = ariaParts.join(', ');

  const badge360 = row.video_url
    ? `<span class="diamond-card__badge-360" aria-hidden="true">
         <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3.5-7.1"/><path d="M21 4v5h-5"/></svg>
         <span class="diamond-card__badge-360-label">360°</span>
       </span>`
    : '';

  card.innerHTML = `
    <a href="${esc(href)}" class="diamond-card__link" aria-label="${esc(ariaLabel)}">
      <div class="diamond-card__media" aria-hidden="true">
        <div class="diamond-card__shape">${mediaInner}</div>
        ${badge360}
        <button type="button" class="diamond-card__favorite" aria-label="Save to favorites" tabindex="-1" data-favorite>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
        </button>
      </div>
      <div class="diamond-card__body">
        <div class="diamond-card__title-row">
          <p class="diamond-card__title">
            <span class="diamond-card__shape-label">${esc(shapeLabel)}</span>
            <span class="diamond-card__title-divider" aria-hidden="true">·</span>
            <span class="diamond-card__carat">${esc(caratText)}ct</span>
          </p>
          <p class="diamond-card__price">${esc(formatPrice(row.price))}</p>
        </div>
        <dl class="diamond-card__specs" aria-label="Diamond specifications">
          <div class="diamond-card__spec">
            <dt class="diamond-card__spec-label">Color</dt>
            <dd class="diamond-card__spec-value">${esc(row.color || '—')}</dd>
          </div>
          <div class="diamond-card__spec">
            <dt class="diamond-card__spec-label">Clarity</dt>
            <dd class="diamond-card__spec-value">${esc(row.clarity || '—')}</dd>
          </div>
          <div class="diamond-card__spec">
            <dt class="diamond-card__spec-label">Cut</dt>
            <dd class="diamond-card__spec-value">${esc(cutDisplay)}</dd>
          </div>
        </dl>
        <span class="diamond-card__cta" aria-hidden="true">View diamond</span>
      </div>
    </a>
  `;
  return card;
}

/** Build a single loading skeleton card — same outer geometry as a real card. */
function buildSkeletonCard() {
  const card = document.createElement('div');
  card.className = 'diamond-card diamond-card-skeleton';
  card.setAttribute('aria-hidden', 'true');
  card.innerHTML = `
    <div class="diamond-card-skeleton__media"></div>
    <div class="diamond-card-skeleton__body">
      <div class="diamond-card-skeleton__line diamond-card-skeleton__line--title"></div>
      <div class="diamond-card-skeleton__line diamond-card-skeleton__line--specs"></div>
    </div>
  `;
  return card;
}

// -----------------------------------------------------------------------------
// DiamondFilters — the form, with is="diamond-filters"
// -----------------------------------------------------------------------------

class DiamondFilters extends HTMLFormElement {
  constructor() {
    super();
    this._onInput = this._onInput.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    const commit = () => this._commit(/* push */ false);
    // Two debounced commits sharing one handler so whichever one fires
    // first wins and we don't double-fetch when the user both drags a
    // slider and types into the linked number input.
    this._debouncedReplace = debounce(commit, DEBOUNCE_MS);
    this._debouncedReplaceTyping = debounce(commit, DEBOUNCE_TYPING_MS);
  }

  connectedCallback() {
    this.addEventListener('input', this._onInput);
    this.addEventListener('change', this._onChange);
    this.addEventListener('submit', this._onSubmit);
    this._wireColorTabs();
    // Seed from URL on first mount so server-rendered markup matches state.
    this._hydrateFromUrl();
    this._updatePillCounts();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this._onInput);
    this.removeEventListener('change', this._onChange);
    this.removeEventListener('submit', this._onSubmit);
  }

  _onInput(event) {
    // Slider drags and number-input typing fire 'input' → replaceState only.
    const t = event.target;
    if (!t || !t.name) return;
    if (t.type === 'range') {
      // Slider drags fire many input events per second — short debounce.
      this._debouncedReplace();
    } else if (t.type === 'number') {
      // Typed characters in carat/price number inputs land here. At normal
      // typing speed the 250ms window lapses between keystrokes and fires
      // a fetch mid-word, so use a longer debounce to let the user finish.
      // (The matching `change` event on blur/Enter still commits with a
      // pushState via `_onChange`, so pressing Enter remains instant.)
      this._debouncedReplaceTyping();
    }
  }

  _onChange(event) {
    // Checkbox / select changes are discrete — pushState so Back works.
    const t = event.target;
    if (!t || !t.name) return;
    this._updatePillCounts();
    this._commit(/* push */ true);
  }

  /**
   * For each `<details class="diamond-filter" data-filter="<name>">` summary
   * pill in this form, write the active count badge text. Hide the badge if
   * the count is zero. The pill containers are siblings of the form, not
   * descendants — find them by walking the form's parent toolbar.
   */
  _updatePillCounts() {
    const params = this.getParams();
    const root = this.closest('.diamond-filters--inline') || this;
    const pills = root.querySelectorAll('.diamond-filter[data-filter]');
    for (const pill of pills) {
      const key = pill.dataset.filter;
      if (!key) continue;
      const badge = pill.querySelector('[data-active-count]');
      if (!badge) continue;
      let count;
      if (key === 'color') {
        // Color pill badges count both tabs combined: D–J white grades
        // (which the GradeScale translates into a checkbox subset), fancy
        // chips, and the intensity range (counted as 1 if any bound is
        // set). This way the user sees a single truthful indicator on the
        // pill regardless of which tab is active.
        const whiteCount = Array.isArray(params.color) ? params.color.length : 0;
        const fancyCount = Array.isArray(params.fancy_color)
          ? params.fancy_color.length
          : 0;
        const intensityActive =
          params.intensity_min != null || params.intensity_max != null;
        count = whiteCount + fancyCount + (intensityActive ? 1 : 0);
      } else {
        const value = params[key];
        count = Array.isArray(value) ? value.length : 0;
      }
      if (count > 0) {
        badge.textContent = String(count);
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }
  }

  /**
   * Tab wiring for the White / Fancy color popover. Runs once in
   * connectedCallback. The markup is inside a
   * `<div data-color-tabs>` container with two `[data-color-tab]` buttons
   * and two `[data-color-tab-panel]` panels. Inline (desktop popover) and
   * drawer (mobile stacked) contexts both use the same structure.
   *
   * Clicking a tab:
   *   1. Clears the OTHER tab's state (unchecks its inputs + notifies
   *      GradeScales to snap back to full-range = no filter).
   *   2. Swaps `aria-selected` + `hidden` on the tab panels.
   *   3. Dispatches one form-level change so the search refetches.
   *
   * Why clear instead of preserve? The White (D–J) and Fancy tabs are
   * mutually exclusive — the backend's `parseFilters` clears the
   * plain-white color allowlist whenever any fancy param is present. If
   * we left stale White chips checked behind a hidden panel, the UI pill
   * count would lie and the user would be unable to remove them without
   * flipping tabs back. Clearing on switch keeps state truthful.
   */
  _wireColorTabs() {
    const tabs = this.querySelectorAll('[data-color-tabs]');
    for (const container of tabs) {
      const buttons = container.querySelectorAll('[data-color-tab]');
      for (const btn of buttons) {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const name = btn.dataset.colorTab;
          if (!name) return;
          this._syncColorTabs(name, { silent: false, container });
        });
      }
    }
  }

  /**
   * Set the active color tab across both inline + drawer contexts. When
   * `silent` is false, clears the OPPOSITE tab's inputs and commits a
   * form change. When `silent` is true (used by _hydrateFromUrl), just
   * flips visibility — no clearing, no commit.
   */
  _syncColorTabs(name, { silent = false, container = null } = {}) {
    const containers = container
      ? [container]
      : Array.from(this.querySelectorAll('[data-color-tabs]'));
    if (containers.length === 0) return;

    let stateChanged = false;
    for (const c of containers) {
      const buttons = c.querySelectorAll('[data-color-tab]');
      const panels = c.querySelectorAll('[data-color-tab-panel]');
      for (const btn of buttons) {
        const active = btn.dataset.colorTab === name;
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      }
      for (const panel of panels) {
        const active = panel.dataset.colorTabPanel === name;
        panel.hidden = !active;
      }

      if (!silent) {
        // Clear the inactive tab's state so the two facets stay mutually
        // exclusive. Scales: uncheck every box and repaint full-range.
        const inactivePanel = c.querySelector(
          `[data-color-tab-panel]:not([data-color-tab-panel="${name}"])`,
        );
        if (inactivePanel) {
          const cleared = this._clearPanelState(inactivePanel);
          if (cleared) stateChanged = true;
        }
      }
    }

    if (!silent && stateChanged) {
      this._updatePillCounts();
      this._commit(/* push */ true);
    }
  }

  /**
   * Uncheck every checkbox inside a tab panel and snap any grade-scale
   * thumbs back to the full range. Returns true if anything actually
   * changed (so the caller can decide whether to commit).
   */
  _clearPanelState(panel) {
    let changed = false;
    const boxes = panel.querySelectorAll('input[type="checkbox"]');
    for (const box of boxes) {
      if (box.checked) {
        box.checked = false;
        changed = true;
      }
    }
    // Reset each GradeScale inside the panel so its visual thumbs match.
    const scales = panel.querySelectorAll('grade-scale');
    for (const scale of scales) {
      if (typeof scale.syncFromCheckboxes === 'function') {
        scale.syncFromCheckboxes();
      }
    }
    return changed;
  }

  _onSubmit(event) {
    event.preventDefault();
    this._commit(/* push */ true);
  }

  /** Collect current form values into the normalised params shape. */
  getParams() {
    const fd = new FormData(this);
    const out = {};
    // multi-valued keys → arrays
    for (const key of MULTI_KEYS) {
      const vals = fd.getAll(key).map(String).filter((v) => v !== '');
      if (vals.length) out[key] = vals;
    }
    // single-valued keys
    const single = ['carat_min', 'carat_max', 'price_min', 'price_max',
      'table_min', 'table_max', 'ratio_min', 'ratio_max',
      'depth_min', 'depth_max'];
    for (const key of single) {
      const v = fd.get(key);
      if (v != null && v !== '') out[key] = String(v);
    }

    // Intensity range: derived from the contiguous checked subset of the
    // hidden `fancy_intensity_token` checkboxes driven by the fancy
    // intensity GradeScale. The GradeScale already enforces "full range =
    // all unchecked", so any checked state here is a real user filter.
    // Surface it as (intensity_min, intensity_max) single-valued URL keys —
    // the backend translates the range into an enumerated IN list.
    const intensityTokens = fd.getAll('fancy_intensity_token').map(String);
    if (intensityTokens.length > 0) {
      // Checkboxes appear in FormData in document order, which matches
      // FANCY_INTENSITY_TOKENS order, so first = min and last = max.
      out.intensity_min = intensityTokens[0];
      out.intensity_max = intensityTokens[intensityTokens.length - 1];
    }

    // sort is a select named `sort_by` in the form — normalise to `sort`
    const sortBy = fd.get('sort_by');
    if (sortBy) out.sort = String(sortBy);
    return out;
  }

  /** Populate form controls from the current URL. */
  _hydrateFromUrl() {
    const params = searchToParams(location.search);

    // Derive the contiguous fancy_intensity_token subset from
    // (intensity_min, intensity_max) BEFORE walking form elements, so the
    // checkboxes are in the right state when GradeScale.syncFromCheckboxes
    // reads them in its queueMicrotask on upgrade.
    let intensitySubset = null;
    if (params.intensity_min != null || params.intensity_max != null) {
      const lo = params.intensity_min
        ? FANCY_INTENSITY_TOKENS.indexOf(params.intensity_min)
        : 0;
      const hi = params.intensity_max
        ? FANCY_INTENSITY_TOKENS.indexOf(params.intensity_max)
        : FANCY_INTENSITY_TOKENS.length - 1;
      if (lo !== -1 && hi !== -1) {
        const [a, b] = lo <= hi ? [lo, hi] : [hi, lo];
        intensitySubset = new Set(FANCY_INTENSITY_TOKENS.slice(a, b + 1));
      }
    }

    for (const el of this.elements) {
      if (!el.name) continue;
      if (el.name === 'fancy_intensity_token') {
        el.checked = intensitySubset != null && intensitySubset.has(el.value);
        continue;
      }
      if (el.type === 'checkbox') {
        const list = params[el.name];
        if (Array.isArray(list)) el.checked = list.includes(el.value);
      } else if (el.name === 'sort_by' && params.sort) {
        el.value = params.sort;
      } else if (params[el.name] != null && !Array.isArray(params[el.name])) {
        el.value = params[el.name];
      }
    }

    // If the URL carries any fancy state, flip the color popover to the
    // Fancy tab so the user lands on the tab whose inputs actually reflect
    // the current search. Done after checkbox hydration so `hasFancyState`
    // sees the freshly-set form values.
    const hasFancyState =
      (Array.isArray(params.fancy_color) && params.fancy_color.length > 0) ||
      intensitySubset != null;
    this._syncColorTabs(hasFancyState ? 'fancy' : 'white', { silent: true });
  }

  /**
   * Commit the current form state into the URL and broadcast a
   * `diamond:filter-change` event so DiamondResults can refetch.
   */
  _commit(push) {
    const params = this.getParams();
    // Any filter change drops the cursor — we want page 1 again.
    delete params.cursor;
    const sp = paramsToSearch(params);
    const newSearch = sp.toString() ? `?${sp.toString()}` : location.pathname;
    const newUrl = location.pathname + (sp.toString() ? `?${sp.toString()}` : '');

    if (push) {
      history.pushState({}, '', newUrl);
    } else {
      history.replaceState({}, '', newUrl);
    }

    document.dispatchEvent(new CustomEvent('diamond:filter-change', {
      detail: { params, source: this.dataset.context || 'inline' },
    }));
  }
}

if (!customElements.get('diamond-filters')) {
  customElements.define('diamond-filters', DiamondFilters, { extends: 'form' });
}

// -----------------------------------------------------------------------------
// GradeScale — dual-thumb range slider over an ordered list of grades.
//
// Drives a stack of hidden `<input type="checkbox" name="<field>">` elements
// so the FormData / URL / pill-count contract used by <diamond-filters> stays
// unchanged. The checkboxes checked at any moment represent a CONTIGUOUS
// subset [minIdx..maxIdx] of `data-grades`. When the slider covers the full
// range, we uncheck every box so the filter is effectively off.
// -----------------------------------------------------------------------------

class GradeScale extends HTMLElement {
  connectedCallback() {
    const raw = (this.dataset.grades || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (raw.length < 2) return;
    this._grades = raw;
    this._minInput = this.querySelector('input[data-thumb="min"]');
    this._maxInput = this.querySelector('input[data-thumb="max"]');
    if (!this._minInput || !this._maxInput) return;
    this._checkboxes = Array.from(this.querySelectorAll('input[type="checkbox"]'));
    if (this._checkboxes.length !== raw.length) return;

    this._onMin = this._onRangeInput.bind(this, 'min');
    this._onMax = this._onRangeInput.bind(this, 'max');
    this._onChange = this._onRangeChange.bind(this);
    // `input` events fire per pixel of drag — we use them only to repaint
    // the visual fill, NOT to commit state. `change` events fire once on
    // thumb release, and that's when we mutate the hidden checkboxes +
    // dispatch the form-level change event that triggers the API call.
    this._minInput.addEventListener('input', this._onMin);
    this._maxInput.addEventListener('input', this._onMax);
    this._minInput.addEventListener('change', this._onChange);
    this._maxInput.addEventListener('change', this._onChange);

    // Sync initial thumb positions from whatever checkboxes are checked
    // (the outer form has already hydrated them from the URL). Defer one
    // microtask so that if the upgrade order ever flips, we still win.
    queueMicrotask(() => this.syncFromCheckboxes());
  }

  disconnectedCallback() {
    if (this._minInput) {
      this._minInput.removeEventListener('input', this._onMin);
      this._minInput.removeEventListener('change', this._onChange);
    }
    if (this._maxInput) {
      this._maxInput.removeEventListener('input', this._onMax);
      this._maxInput.removeEventListener('change', this._onChange);
    }
  }

  /** Read the checked subset of this.checkboxes and snap the thumbs to its
   *  contiguous bounds. Non-contiguous selections (e.g. from a stale shared
   *  link) are snapped to [min…max] of the checked indices. */
  syncFromCheckboxes() {
    const n = this._grades.length;
    let minIdx = -1;
    let maxIdx = -1;
    for (let i = 0; i < n; i++) {
      if (this._checkboxes[i].checked) {
        if (minIdx === -1) minIdx = i;
        maxIdx = i;
      }
    }
    if (minIdx === -1) {
      // No checkboxes checked = no filter = show the full range in the UI.
      minIdx = 0;
      maxIdx = n - 1;
    }
    this._applyRange(minIdx, maxIdx, /* silent */ true);
  }

  _onRangeInput(which) {
    // Drag tick — visual only. We repaint the fill bar and clamp the
    // idle thumb if the dragged one tries to cross it, but we do NOT
    // mutate the hidden checkboxes or dispatch any event. Committing
    // per-tick would fire an API call for every intermediate step the
    // thumb passes through; the merchant only wants a call on release.
    let a = Number(this._minInput.value);
    let b = Number(this._maxInput.value);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return;
    if (a > b) {
      // Thumbs crossed — clamp the one the user is NOT dragging.
      if (which === 'min') b = a;
      else a = b;
    }
    this._paintRange(a, b);
  }

  _onRangeChange() {
    // Thumb released — commit the final min/max into the hidden checkbox
    // state and dispatch the form-level change event. This fires exactly
    // once per drag gesture, so the API sees one request.
    let a = Number(this._minInput.value);
    let b = Number(this._maxInput.value);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return;
    if (a > b) [a, b] = [b, a];
    this._applyRange(a, b, /* silent */ false);
  }

  /** Visual-only thumb + fill paint. Clamps indices into range, writes
   *  the range input values (so the native thumb position matches), and
   *  updates the CSS custom properties that drive the fill bar. Does NOT
   *  touch checkbox state — callers that need a commit go through
   *  `_applyRange` instead. */
  _paintRange(minIdx, maxIdx) {
    const n = this._grades.length;
    const last = n - 1;
    minIdx = Math.max(0, Math.min(last, minIdx));
    maxIdx = Math.max(0, Math.min(last, maxIdx));
    if (minIdx > maxIdx) [minIdx, maxIdx] = [maxIdx, minIdx];

    this._minInput.value = String(minIdx);
    this._maxInput.value = String(maxIdx);

    // Ratios (0..1) feed into the CSS calc() that insets the fill by
    // thumb-radius on each side, matching native range-thumb travel.
    const minRatio = last === 0 ? 0 : minIdx / last;
    const maxRatio = last === 0 ? 1 : maxIdx / last;
    this.style.setProperty('--grade-scale-min-ratio', String(minRatio));
    this.style.setProperty('--grade-scale-max-ratio', String(maxRatio));
  }

  _applyRange(minIdx, maxIdx, silent) {
    this._paintRange(minIdx, maxIdx);

    // Re-read the clamped values back off the inputs (_paintRange mutated
    // them) so the checkbox sync uses the exact same bounds as the paint.
    minIdx = Number(this._minInput.value);
    maxIdx = Number(this._maxInput.value);
    const n = this._grades.length;
    const last = n - 1;

    // Full range = "no filter": uncheck every box so FormData omits the key.
    const isFull = minIdx === 0 && maxIdx === last;
    let changed = false;
    for (let i = 0; i < n; i++) {
      const cb = this._checkboxes[i];
      const want = !isFull && i >= minIdx && i <= maxIdx;
      if (cb.checked !== want) {
        cb.checked = want;
        changed = true;
      }
    }
    if (changed && !silent) {
      // Bubble a change event so <diamond-filters> commits the new URL.
      // IMPORTANT: dispatch from a checkbox, not from `this`. The form's
      // `_onChange` handler early-exits when `event.target.name` is
      // falsy, and a <grade-scale> custom element has no `name` property.
      // Any checkbox in this scale carries the correct `name` attribute.
      this._checkboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('grade-scale')) {
  customElements.define('grade-scale', GradeScale);
}

// -----------------------------------------------------------------------------
// DiamondRange — self-contained dual-thumb numeric range (price + carat).
//
// Why not reuse Prestige's <price-range> custom element? Its internal
// querySelectors look up `input[name="filter.v.price.gte"]` / `.lte`, which
// match Shopify collection-page filter inputs — NOT our `carat_min` /
// `price_min` names. When those lookups return null, the connectedCallback
// throws before wiring the slider↔number-input sync, and Prestige's
// `<carat-range>` tag isn't a registered custom element at all (it never
// upgrades). This class replaces both and also fixes the `--range-min`
// computation to use `(value - min) / (max - min)` so thumb travel lines
// up correctly for ranges whose `min` is not zero (e.g. carat 0.3–33).
//
// Markup contract (see snippets/diamond-filters.liquid):
//   <diamond-range data-min=".." data-max=".." data-step=".." data-decimals="..">
//     <div class="range-group" style="--range-min:0%;--range-max:100%">
//       <input type="range" data-range="min" ...>
//       <input type="range" data-range="max" ...>
//     </div>
//     <div class="input-group">
//       <input data-number="min" name="..._min" ...>
//       <input data-number="max" name="..._max" ...>
//     </div>
//   </diamond-range>
// -----------------------------------------------------------------------------

class DiamondRange extends HTMLElement {
  connectedCallback() {
    this._min = Number(this.dataset.min);
    this._max = Number(this.dataset.max);
    this._step = Number(this.dataset.step) || 1;
    this._decimals = Number(this.dataset.decimals) || 0;
    if (!Number.isFinite(this._min) || !Number.isFinite(this._max) || this._max <= this._min) {
      return;
    }

    this._rangeMin = this.querySelector('input[type="range"][data-range="min"]');
    this._rangeMax = this.querySelector('input[type="range"][data-range="max"]');
    this._numberMin = this.querySelector('input[type="number"][data-number="min"]');
    this._numberMax = this.querySelector('input[type="number"][data-number="max"]');
    this._rangeGroup = this.querySelector('.range-group');
    if (!this._rangeMin || !this._rangeMax || !this._numberMin || !this._numberMax || !this._rangeGroup) {
      return;
    }

    this._onRangeInput = this._onRangeInput.bind(this);
    this._onRangeChange = this._onRangeChange.bind(this);
    this._onNumberInput = this._onNumberInput.bind(this);
    this._onNumberChange = this._onNumberChange.bind(this);

    // Slider drag: fire while dragging (debounced replaceState in the form),
    // and again on release (pushState pass through form _onChange).
    this._rangeMin.addEventListener('input', this._onRangeInput);
    this._rangeMax.addEventListener('input', this._onRangeInput);
    this._rangeMin.addEventListener('change', this._onRangeChange);
    this._rangeMax.addEventListener('change', this._onRangeChange);
    // Number input: mirror into slider on every keystroke; clamp + commit
    // on blur / Enter so we don't fight the user mid-type.
    this._numberMin.addEventListener('input', this._onNumberInput);
    this._numberMax.addEventListener('input', this._onNumberInput);
    this._numberMin.addEventListener('change', this._onNumberChange);
    this._numberMax.addEventListener('change', this._onNumberChange);

    // Seed from the number inputs (which `<diamond-filters>._hydrateFromUrl`
    // has already populated from the URL). Numbers are the source of truth
    // on mount because range inputs have no `name` and aren't hydrated.
    const initLo = Number(this._numberMin.value);
    const initHi = Number(this._numberMax.value);
    if (Number.isFinite(initLo)) this._rangeMin.value = String(initLo);
    if (Number.isFinite(initHi)) this._rangeMax.value = String(initHi);
    this._paintFill();
  }

  /** Convert a numeric value to its `--range-*` percentage along the track.
   *  Uses `(v - min) / (max - min)` so non-zero-min ranges track correctly. */
  _pct(value) {
    const span = this._max - this._min;
    if (span <= 0) return 0;
    const clamped = Math.max(this._min, Math.min(this._max, value));
    return ((clamped - this._min) / span) * 100;
  }

  _paintFill() {
    const lo = Number(this._rangeMin.value);
    const hi = Number(this._rangeMax.value);
    this._rangeGroup.style.setProperty('--range-min', this._pct(lo) + '%');
    this._rangeGroup.style.setProperty('--range-max', this._pct(hi) + '%');
  }

  _onRangeInput(event) {
    let lo = Number(this._rangeMin.value);
    let hi = Number(this._rangeMax.value);
    // Keep thumbs from crossing: clamp the idle thumb one step away from the
    // dragged one. One `step` guarantees minIdx < maxIdx.
    if (lo > hi - this._step) {
      if (event.target === this._rangeMin) {
        lo = Math.max(this._min, hi - this._step);
        this._rangeMin.value = String(lo);
      } else {
        hi = Math.min(this._max, lo + this._step);
        this._rangeMax.value = String(hi);
      }
    }
    // Mirror slider → number inputs in real time so the user sees the value
    // change as they drag. Visual only — we deliberately do NOT dispatch
    // an input/change event here. Per-tick commits would fire an API call
    // for every intermediate step the thumb passes through; the merchant
    // only wants one call when the thumb is released (see `_onRangeChange`).
    this._numberMin.value = this._format(lo);
    this._numberMax.value = this._format(hi);
    this._paintFill();
  }

  _onRangeChange() {
    // Slider released — commit with pushState so the user can use the back
    // button to undo the drag. Dispatched from a named number input for the
    // same reason as above.
    this._numberMin.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _onNumberInput(event) {
    // Mirror typing → slider in real time (no clamping yet).
    const v = Number(event.target.value);
    if (!Number.isFinite(v)) return;
    if (event.target === this._numberMin) {
      this._rangeMin.value = String(v);
    } else {
      this._rangeMax.value = String(v);
    }
    this._paintFill();
  }

  _onNumberChange() {
    // Final clamp pass on blur/Enter. Make sure min < max and both are in
    // bounds; push the clamped values back into both inputs.
    let lo = Number(this._numberMin.value);
    let hi = Number(this._numberMax.value);
    if (!Number.isFinite(lo)) lo = this._min;
    if (!Number.isFinite(hi)) hi = this._max;
    lo = Math.max(this._min, Math.min(this._max, lo));
    hi = Math.max(this._min, Math.min(this._max, hi));
    if (lo > hi) [lo, hi] = [hi, lo];
    if (hi - lo < this._step) hi = Math.min(this._max, lo + this._step);

    this._numberMin.value = this._format(lo);
    this._numberMax.value = this._format(hi);
    this._rangeMin.value = String(lo);
    this._rangeMax.value = String(hi);
    this._paintFill();
  }

  _format(v) {
    return Number(v).toFixed(this._decimals);
  }
}

if (!customElements.get('diamond-range')) {
  customElements.define('diamond-range', DiamondRange);
}

// -----------------------------------------------------------------------------
// DiamondResults — the grid, infinite scroll, fetch lifecycle
// -----------------------------------------------------------------------------

class DiamondResults extends HTMLElement {
  constructor() {
    super();
    this._abort = null;
    this._io = null;
    this._sentinel = null;
    this._loading = false;
    this._hasMore = false;
    this._nextCursor = null;
    this._currentParams = null;
    this._total = 0;

    this._onFilterChange = this._onFilterChange.bind(this);
    this._onPopState = this._onPopState.bind(this);
    this._onScroll = this._onScroll.bind(this);
  }

  connectedCallback() {
    this._grid = this.querySelector('.diamond-results__grid');
    this._countEl = this.querySelector('.diamond-results__count');
    this._emptyEl = this.querySelector('.diamond-results__empty');
    this._errorEl = this.querySelector('.diamond-results__error');
    this._ensureSentinel();
    this._observeSentinel();

    document.addEventListener('diamond:filter-change', this._onFilterChange);
    window.addEventListener('popstate', this._onPopState);
    // Scroll-event fallback for infinite load. IntersectionObserver is the
    // primary trigger (cheaper, fires without layout thrash), but we've
    // seen it silently miss in some environments (nested scroll containers,
    // stacking-context quirks, throttled hidden tabs). The scroll listener
    // mirrors the same check via getBoundingClientRect so pagination works
    // no matter what. Both paths hit `_maybeLoadMore` which is idempotent.
    window.addEventListener('scroll', this._onScroll, { passive: true });

    // Always do an initial live fetch on mount — the server-rendered cards
    // are stand-ins, not real DB data. Paint skeletons first so the grid
    // doesn't collapse while the network round-trip is in flight.
    const params = searchToParams(location.search);
    this._currentParams = params;
    this._showSkeletons(DEFAULT_LIMIT);
    this._reload(params);
  }

  disconnectedCallback() {
    document.removeEventListener('diamond:filter-change', this._onFilterChange);
    window.removeEventListener('popstate', this._onPopState);
    window.removeEventListener('scroll', this._onScroll);
    if (this._io) this._io.disconnect();
    if (this._abort) this._abort.abort();
  }

  /** Scroll-event fallback. Cheap: one getBoundingClientRect + arithmetic
   *  per scroll event. Gated on `_loading` so most scroll events are
   *  no-ops while a fetch is in flight. Trigger distance matches the
   *  IntersectionObserver rootMargin (600px) so the two paths behave
   *  identically from the user's perspective. */
  _onScroll() {
    this._maybeLoadMore();
  }

  _maybeLoadMore() {
    if (this._loading || !this._hasMore || !this._nextCursor) return;
    if (!this._sentinel || !this._sentinel.isConnected) return;
    const rect = this._sentinel.getBoundingClientRect();
    const trigger = window.innerHeight + 600;
    if (rect.top <= trigger) {
      this._loadMore();
    }
  }

  _ensureSentinel() {
    let s = this.querySelector('.diamond-load-sentinel');
    if (!s) {
      s = document.createElement('div');
      s.className = 'diamond-load-sentinel';
      s.setAttribute('aria-hidden', 'true');
      // Append after the grid but before the empty-state element.
      if (this._grid && this._grid.parentNode) {
        this._grid.parentNode.insertBefore(s, this._grid.nextSibling);
      } else {
        this.appendChild(s);
      }
    }
    this._sentinel = s;
  }

  _observeSentinel() {
    if (this._io) this._io.disconnect();
    this._io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        // Route through `_maybeLoadMore` so the IO path and the scroll-event
        // fallback share one guarded entry point. The `isIntersecting` check
        // here is just a cheap early-out — the real gate lives in
        // `_maybeLoadMore`, which re-validates against a fresh bounding rect.
        if (e.isIntersecting) this._maybeLoadMore();
      }
    }, { rootMargin: '600px' });
    if (this._sentinel) this._io.observe(this._sentinel);
  }

  _onFilterChange(event) {
    const params = event.detail?.params || searchToParams(location.search);
    this._currentParams = params;
    this._reload(params);
  }

  _onPopState() {
    const params = searchToParams(location.search);
    if (this._currentParams && sameParams(this._currentParams, params)) return;
    this._currentParams = params;
    // Re-sync all filter forms to the new URL, then reload.
    for (const form of document.querySelectorAll('form[is="diamond-filters"]')) {
      if (typeof form._hydrateFromUrl === 'function') form._hydrateFromUrl();
    }
    this._reload(params);
  }

  async _reload(params) {
    const reqParams = { ...params };
    delete reqParams.cursor;
    reqParams.limit = String(DEFAULT_LIMIT);

    this._cancelInFlight();
    this._abort = new AbortController();
    this._loading = true;
    this._hideError();
    this._setGridLoading(true);
    document.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const data = await fetchDiamonds(reqParams, { signal: this._abort.signal });
      this._renderReplace(data);
      // Fire-and-forget count request so the "N diamonds found" header
      // reflects the true total, not just the page size. Uses a separate
      // controller so the main fetch isn't tied to the count.
      this._fetchTotal(params);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('[diamond-search] reload failed', err);
      this._renderError(err, () => this._reload(params));
    } finally {
      this._loading = false;
      this._setGridLoading(false);
      document.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  async _fetchTotal(params) {
    if (this._countAbort) {
      try { this._countAbort.abort(); } catch (_) { /* ignore */ }
    }
    this._countAbort = new AbortController();
    try {
      const data = await fetchDiamonds(params, {
        signal: this._countAbort.signal,
        countOnly: true,
      });
      this._total = data.total || 0;
      this._updateCount(this._total);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      // Count is non-critical — hide the loading state gracefully.
      console.warn('[diamond-search] count fetch failed', err);
      this._showCountLoading();
    }
  }

  async _loadMore() {
    if (!this._hasMore || !this._nextCursor) return;
    const reqParams = { ...(this._currentParams || {}) };
    reqParams.cursor = this._nextCursor;
    reqParams.limit = String(DEFAULT_LIMIT);

    this._cancelInFlight();
    this._abort = new AbortController();
    this._loading = true;
    document.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const data = await fetchDiamonds(reqParams, { signal: this._abort.signal });
      this._renderAppend(data);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('[diamond-search] loadMore failed', err);
      // Append-path errors don't clobber the existing grid — surface a
      // non-blocking retry on the sentinel area.
      this._renderError(err, () => this._loadMore(), /* inline */ true);
    } finally {
      this._loading = false;
      document.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  _cancelInFlight() {
    if (this._abort) {
      try { this._abort.abort(); } catch (_) { /* ignore */ }
      this._abort = null;
    }
  }

  _showSkeletons(count) {
    if (!this._grid) return;
    this._grid.replaceChildren();
    for (let i = 0; i < count; i++) this._grid.appendChild(buildSkeletonCard());
    this._updateEmpty(false);
  }

  _setGridLoading(loading) {
    if (!this._grid) return;
    this._grid.classList.toggle('diamond-results__grid--loading', !!loading);
    // aria-busy is the canonical hook for assistive tech: screen readers
    // will suppress grid content changes until the loading pass settles.
    this._grid.setAttribute('aria-busy', loading ? 'true' : 'false');
    // Also mirror the state on the host <diamond-results> element so the
    // results region as a whole is marked busy during a refetch.
    this.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading) {
      document.body.dataset.diamondLoading = 'true';
    } else {
      delete document.body.dataset.diamondLoading;
    }
  }

  _hideError() {
    if (this._errorEl) {
      this._errorEl.hidden = true;
      this._errorEl.replaceChildren();
    }
  }

  /**
   * Render the error block. For `inline=true` (infinite-scroll append
   * failures) the grid is preserved and we only show a retry affordance
   * under the sentinel. For the full-page path the grid is wiped.
   */
  _renderError(err, retry, inline = false) {
    const isServerOrNetwork =
      !err || !err.status || err.kind === 'server' || err.kind === 'network';
    const headline = isServerOrNetwork
      ? 'Something went wrong'
      : (err && err.message) || 'That filter combination is not valid';
    const subline = isServerOrNetwork
      ? 'We could not load diamonds right now. Please try again.'
      : 'Please adjust your filters and try again.';

    const el = this._ensureErrorEl();
    el.hidden = false;
    el.replaceChildren();

    const h = document.createElement('p');
    h.className = 'diamond-results__error-headline bold';
    h.textContent = headline;
    el.appendChild(h);

    const s = document.createElement('p');
    s.className = 'diamond-results__error-sub text-subdued';
    s.textContent = subline;
    el.appendChild(s);

    const actions = document.createElement('div');
    actions.className = 'diamond-results__error-actions';

    const retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.className = 'button button--primary';
    retryBtn.textContent = 'Try again';
    retryBtn.addEventListener('click', () => {
      this._hideError();
      if (typeof retry === 'function') retry();
    });
    actions.appendChild(retryBtn);

    if (isServerOrNetwork) {
      const calendlyUrl = this.dataset.calendlyUrl
        || document.documentElement.dataset.calendlyUrl
        || 'https://calendly.com';
      const a = document.createElement('a');
      a.className = 'button button--outline';
      a.href = calendlyUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = 'Book consultation';
      actions.appendChild(a);
    }

    el.appendChild(actions);

    if (!inline && this._grid) {
      this._grid.replaceChildren();
      this._grid.hidden = true;
    }
  }

  _ensureErrorEl() {
    if (this._errorEl && this._errorEl.isConnected) return this._errorEl;
    let el = this.querySelector('.diamond-results__error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'diamond-results__error';
      el.setAttribute('role', 'alert');
      el.hidden = true;
      if (this._grid && this._grid.parentNode) {
        this._grid.parentNode.insertBefore(el, this._grid.nextSibling);
      } else {
        this.appendChild(el);
      }
    }
    this._errorEl = el;
    return el;
  }

  _renderReplace(data) {
    if (!this._grid) return;
    this._hideError();
    this._grid.hidden = false;
    this._grid.replaceChildren();
    for (const row of data.rows) this._grid.appendChild(buildCardElement(row));
    this._hasMore = !!data.hasMore;
    this._nextCursor = data.nextCursor || null;
    if (data.rows.length === 0) {
      this._total = 0;
      this._updateCount(0);
    } else {
      this._total = 0;
      this._showCountLoading();
    }
    this._updateEmpty(data.rows.length === 0);
    // Re-anchor the sentinel after the (new) grid contents.
    this._ensureSentinel();
    this._observeSentinel();
  }

  _renderAppend(data) {
    if (!this._grid) return;
    for (const row of data.rows) this._grid.appendChild(buildCardElement(row));
    this._hasMore = !!data.hasMore;
    this._nextCursor = data.nextCursor || null;
    if (data.total) {
      this._total = data.total;
      this._updateCount(this._total);
    }
    // Re-check after the layout settles. If the new rows didn't push the
    // sentinel out of the trigger zone (tall viewports, short rows),
    // neither IntersectionObserver nor the scroll listener will fire on
    // their own — the state hasn't changed. A deferred tick lets us
    // auto-continue pagination until the sentinel finally exits view.
    requestAnimationFrame(() => this._maybeLoadMore());
  }

  _showCountLoading() {
    if (!this._countEl) return;
    this._countEl.textContent = '\u00a0';
    this._countEl.classList.add('diamond-results__count--loading');
  }

  _updateCount(total) {
    if (!this._countEl) return;
    this._countEl.classList.remove('diamond-results__count--loading');
    const noun = total === 1 ? 'diamond' : 'diamonds';
    this._countEl.textContent = `${total.toLocaleString()} ${noun}`;
  }

  _updateEmpty(isEmpty) {
    if (!this._emptyEl) return;
    this._emptyEl.hidden = !isEmpty;
    if (this._grid) this._grid.hidden = isEmpty;
  }
}

if (!customElements.get('diamond-results')) {
  customElements.define('diamond-results', DiamondResults);
}

// -----------------------------------------------------------------------------
// DiamondCard — lazy hover-video mount, touch two-tap
// -----------------------------------------------------------------------------

class DiamondCard extends HTMLElement {
  constructor() {
    super();
    this._videoMounted = false;
    this._previewActive = false;
    this._inViewport = false;

    this._onPointerEnter = this._onPointerEnter.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onClickCapture = this._onClickCapture.bind(this);
  }

  connectedCallback() {
    this._media = this.querySelector('.diamond-card__media');
    if (!this._media) return;

    this._noImage = !this.querySelector('.diamond-card__image');

    // IntersectionObserver gate: only arm hover if visible.
    this._io = new IntersectionObserver((entries) => {
      for (const e of entries) this._inViewport = e.isIntersecting;
    }, { rootMargin: '200px' });
    this._io.observe(this);

    if (this._noImage && this._videoUrl()) {
      this._mountVideo();
      this._previewActive = true;
      this.dataset.previewActive = 'true';
    }

    this.addEventListener('pointerenter', this._onPointerEnter);
    this.addEventListener('pointerleave', this._onPointerLeave);
    this.addEventListener('touchstart', this._onTouchStart, { passive: true });
    // Capture-phase click so we can intercept the first tap on touch.
    this._media.addEventListener('click', this._onClickCapture, true);
  }

  disconnectedCallback() {
    if (this._io) this._io.disconnect();
    this.removeEventListener('pointerenter', this._onPointerEnter);
    this.removeEventListener('pointerleave', this._onPointerLeave);
    this.removeEventListener('touchstart', this._onTouchStart);
    if (this._media) this._media.removeEventListener('click', this._onClickCapture, true);
    this._unmountVideo();
  }

  _videoUrl() {
    return this.dataset.videoUrl || '';
  }

  _onPointerEnter(event) {
    // Mouse only — touch devices emit pointerenter too but we keep touch in
    // its own two-tap path via _onTouchStart/_onClickCapture.
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!this._inViewport) return;
    this._mountVideo();
  }

  _onPointerLeave(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (this._noImage && this._videoUrl()) return;
    this._unmountVideo();
  }

  _onTouchStart() {
    // Mark the element so the capture-click handler knows touch is active.
    this.dataset.pointerMode = 'touch';
  }

  _onClickCapture(event) {
    if (this.dataset.pointerMode !== 'touch') return;
    if (!this._videoUrl()) return; // no video → let navigation happen
    if (!this._previewActive) {
      event.preventDefault();
      event.stopPropagation();
      this._mountVideo();
      this._previewActive = true;
      this.dataset.previewActive = 'true';
    }
    // second tap falls through → navigate to detail page
  }

  _mountVideo() {
    if (this._videoMounted) return;
    // Respect prefers-reduced-motion for hover previews — but still mount
    // the video on no-image cards since it's their only visual content.
    if (!this._noImage && typeof window !== 'undefined' && window.matchMedia) {
      try {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      } catch (_) { /* ignore */ }
    }
    const url = this._videoUrl();
    if (!url) return;
    const holder = this.querySelector('.diamond-card__media');
    if (!holder) return;

    // Nivoda's `video` field is a loupe360 player PAGE (HTML), not a .mp4
    // file. <video src="..."> cannot render an HTML page; we need an
    // <iframe> for the loupe360 viewer. Detect this by URL host. Fall back
    // to <video> for any future provider that returns a real video file.
    const isLoupe360 = /(?:^|\.)loupe360\.com/i.test(this._safeHostname(url));
    let mediaEl;
    if (isLoupe360) {
      // The loupe360 URL pattern hardcodes the render dimensions in the
      // path: `/video/<W>/<H>?...` (Nivoda generates `/video/500/500/`).
      // The player draws at those exact dimensions, so when our 285px card
      // loads it, the diamond renders larger than the iframe and appears
      // pinned to the bottom-left corner.
      //
      // Rewrite the URL to use the card's actual rendered dimensions so
      // the player adapts. We use a single square dimension based on the
      // card width since the media frame is `aspect-ratio: 1/1`.
      const cardWidth = Math.round(holder.getBoundingClientRect().width) || 320;
      const dim = Math.max(200, Math.min(800, cardWidth));
      const sized = url.replace(
        /\/video\/\d+\/\d+(?=[/?]|$)/,
        `/video/${dim}/${dim}`
      );

      mediaEl = document.createElement('iframe');
      mediaEl.setAttribute('frameborder', '0');
      mediaEl.setAttribute('allow', 'autoplay');
      mediaEl.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
      mediaEl.setAttribute('loading', 'lazy');
      mediaEl.setAttribute('aria-hidden', 'true');

      // The iframe inherits loupe360's white body background even if we set
      // `background: transparent` on the iframe element. To eliminate the
      // white flash entirely we keep the iframe `visibility: hidden` until
      // the player has had time to render its first frame, AND we show a
      // loading spinner overlay on top of the static image while we wait.
      //
      // Sequence:
      //   1. Set card data-video-loading=true → CSS shows spinner overlay
      //   2. Mount iframe visibility:hidden
      //   3. Wait for iframe `load` event (HTML doc + assets ready)
      //   4. Add a 700ms grace for the loupe360 player to actually paint
      //      its first frame inside the iframe document
      //   5. Set card data-video-loading=false → spinner fades out
      //   6. Set iframe visibility:visible → fades in via CSS transition
      mediaEl.style.visibility = 'hidden';
      mediaEl.style.opacity = '0';
      this.dataset.videoLoading = 'true';

      const reveal = () => {
        if (!mediaEl || !mediaEl.isConnected) return;
        // Bail if the user moved away before the player finished booting.
        if (this.dataset.videoLoading !== 'true') return;
        delete this.dataset.videoLoading;
        mediaEl.style.visibility = 'visible';
        // Force a reflow before the opacity change so the transition runs.
        // eslint-disable-next-line no-unused-expressions
        mediaEl.offsetHeight;
        mediaEl.style.opacity = '1';
      };

      mediaEl.addEventListener('load', () => {
        // Player needs ~500-800ms after the iframe document loads to paint
        // its first frame. 700ms hits the sweet spot for most diamonds.
        setTimeout(reveal, 700);
      }, { once: true });

      // Hard fallback: if `load` never fires (network failure / timeout),
      // reveal anyway after 5s so the card never stays in a perpetual
      // loading state.
      setTimeout(reveal, 5000);

      // Set src LAST so the load handler is bound before the request fires.
      mediaEl.src = sized;
    } else {
      mediaEl = document.createElement('video');
      mediaEl.src = url;
      mediaEl.muted = true;
      mediaEl.loop = true;
      mediaEl.playsInline = true;
      mediaEl.preload = 'none';
      mediaEl.setAttribute('aria-hidden', 'true');
    }
    mediaEl.className = 'diamond-card__video';
    holder.appendChild(mediaEl);
    this._video = mediaEl;
    this._videoMounted = true;

    // <video> elements need an explicit play() call. Iframes auto-load.
    if (mediaEl.tagName === 'VIDEO') {
      const p = mediaEl.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked */ });
    }
  }

  _safeHostname(url) {
    try { return new URL(url).hostname; } catch (_) { return ''; }
  }

  _unmountVideo() {
    if (!this._videoMounted) return;
    try { if (this._video && this._video.tagName === 'VIDEO') this._video.pause(); } catch (_) { /* ignore */ }
    if (this._video && this._video.parentNode) {
      this._video.parentNode.removeChild(this._video);
    }
    this._video = null;
    this._videoMounted = false;
    this._previewActive = false;
    delete this.dataset.previewActive;
    delete this.dataset.videoLoading;
  }
}

if (!customElements.get('diamond-card')) {
  customElements.define('diamond-card', DiamondCard);
}

// -----------------------------------------------------------------------------
// DiamondFiltersDrawer — live "Show N" count on the mobile Apply button
// -----------------------------------------------------------------------------

class DiamondFiltersDrawer extends HTMLElement {
  constructor() {
    super();
    this._abort = null;
    this._onFilterChange = this._onFilterChange.bind(this);
    this._onApplyClick = this._onApplyClick.bind(this);
    this._onClearClick = this._onClearClick.bind(this);
    this._recount = debounce(() => this._fetchCount(), DEBOUNCE_MS);
  }

  connectedCallback() {
    // Apply + Clear used to be nested inside this element (when the drawer
    // was a plain <dialog>). After the switch to <x-drawer>, they're
    // direct children of the drawer so they can project into the shadow
    // template's `slot="footer"` — which means they're siblings of this
    // custom element, not descendants. Look them up on the ancestor
    // drawer; fall back to this element for back-compat.
    const drawer = this.closest('x-drawer') || this;
    this._applyBtn = drawer.querySelector('[data-apply-filters]')
      || drawer.querySelector('button[type="submit"]')
      || drawer.querySelector('.button');
    this._clearBtn = drawer.querySelector('[data-clear-filters]');
    this._form = this.querySelector('form[is="diamond-filters"]');

    // Collapsed "Show more shapes" toggle — only rendered in the drawer
    // context by diamond-filters.liquid. Hides shapes 10-34 behind a
    // button so the mobile drawer fits the first 9 in three rows.
    this._shapeMoreBtn = this.querySelector('[data-shape-more-toggle]');
    this._onShapeMoreClick = this._onShapeMoreClick.bind(this);
    if (this._shapeMoreBtn) {
      this._shapeMoreBtn.addEventListener('click', this._onShapeMoreClick);
      // Auto-expand if the URL has pre-selected any secondary shape
      // (e.g. /pages/diamonds?shape=trilliant). Otherwise the selected
      // chip would sit behind the `hidden` attribute and the user
      // couldn't see their own filter.
      const secondaryGroup = this.querySelector('[data-shape-more-content]');
      if (secondaryGroup && secondaryGroup.querySelector('input[type="checkbox"]:checked')) {
        this._onShapeMoreClick();
      }
    }

    document.addEventListener('diamond:filter-change', this._onFilterChange);
    if (this._applyBtn) this._applyBtn.addEventListener('click', this._onApplyClick);
    if (this._clearBtn) this._clearBtn.addEventListener('click', this._onClearClick);
  }

  disconnectedCallback() {
    document.removeEventListener('diamond:filter-change', this._onFilterChange);
    if (this._applyBtn) this._applyBtn.removeEventListener('click', this._onApplyClick);
    if (this._clearBtn) this._clearBtn.removeEventListener('click', this._onClearClick);
    if (this._shapeMoreBtn) this._shapeMoreBtn.removeEventListener('click', this._onShapeMoreClick);
    if (this._abort) this._abort.abort();
  }

  _onShapeMoreClick() {
    const btn = this._shapeMoreBtn;
    if (!btn) return;
    const target = this.querySelector('[data-shape-more-content]');
    if (!target) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    const next = !expanded;
    btn.setAttribute('aria-expanded', next ? 'true' : 'false');
    target.hidden = !next;
    // Swap the button label (collapsed ↔ expanded copy).
    const collapsedLabel = btn.querySelector('[data-shape-more-label-collapsed]');
    const expandedLabel = btn.querySelector('[data-shape-more-label-expanded]');
    if (collapsedLabel) collapsedLabel.hidden = next;
    if (expandedLabel) expandedLabel.hidden = !next;
  }

  _onFilterChange(event) {
    if (event.detail?.source !== 'drawer') return;
    this._recount();
  }

  async _fetchCount() {
    const params = this._form && this._form.getParams ? this._form.getParams() : {};
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    try {
      const data = await fetchDiamonds(params, {
        signal: this._abort.signal,
        countOnly: true,
      });
      this._updateApplyLabel(data.total);
    } catch (err) {
      if (err && err.name !== 'AbortError') {
        console.error('[diamond-search] drawer count failed', err);
      }
    }
  }

  _updateApplyLabel(total) {
    if (!this._applyBtn) return;
    const label = this._applyBtn.querySelector('.button__label')
      || this._applyBtn;
    label.textContent = `Show ${total.toLocaleString()} diamond${total === 1 ? '' : 's'}`;
  }

  _onApplyClick() {
    // The Apply button is rendered with `is="dialog-close-button"`, which
    // dispatches a bubbling `dialog:force-close` event that x-drawer
    // listens for — so the close is already handled upstream. This
    // method is kept as a defensive fallback in case the custom element
    // upgrade races or fails: walk up to the ancestor drawer and call
    // its `hide()` method directly.
    const drawer = this.closest('x-drawer');
    if (drawer && typeof drawer.hide === 'function') {
      queueMicrotask(() => {
        try { drawer.hide(); } catch (_) { /* ignore */ }
      });
    }
  }

  _onClearClick(event) {
    event.preventDefault();
    if (this._form) {
      for (const el of this._form.elements) {
        if (!el.name) continue;
        if (el.type === 'checkbox' || el.type === 'radio') el.checked = false;
        else if (el.tagName === 'SELECT') el.selectedIndex = 0;
        else if (el.type === 'number' || el.type === 'range') el.value = el.defaultValue;
      }
      // Clear URL + broadcast
      history.pushState({}, '', location.pathname);
      document.dispatchEvent(new CustomEvent('diamond:filter-change', {
        detail: { params: {}, source: 'drawer' },
      }));
    }
  }
}

if (!customElements.get('diamond-filters-drawer')) {
  customElements.define('diamond-filters-drawer', DiamondFiltersDrawer);
}

// -----------------------------------------------------------------------------
// Popover dropdowns (filter pills)
//
// Uses native <details class="diamond-filter"> for keyboard/screen-reader
// support. We add two layered behaviors:
//
//   1. Open one popover at a time — opening a new pill closes any others.
//   2. Click outside any open popover → close it.
//
// Both run as document-level listeners attached once per page load. They are
// idempotent so re-mounting custom elements does not double-bind.
// -----------------------------------------------------------------------------

if (typeof document !== 'undefined' && !window.__diamondPopoverWired) {
  window.__diamondPopoverWired = true;

  // (1) Close other open <details class="diamond-filter"> when one opens.
  document.addEventListener('toggle', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches('details.diamond-filter')) return;
    if (!target.open) return;
    const all = document.querySelectorAll('details.diamond-filter[open]');
    for (const el of all) {
      if (el !== target) el.open = false;
    }
  }, true);

  // (2) Click outside any open popover closes it.
  document.addEventListener('click', (event) => {
    const open = document.querySelectorAll('details.diamond-filter[open]');
    if (open.length === 0) return;
    const path = event.composedPath ? event.composedPath() : [event.target];
    for (const el of open) {
      if (!path.includes(el)) el.open = false;
    }
  });

  // (3) Escape key closes any open popover.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelectorAll('details.diamond-filter[open]');
    for (const el of open) el.open = false;
  });
}

// -----------------------------------------------------------------------------
// Public test hooks (non-enumerable on window — used only by debugging)
// -----------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  Object.defineProperty(window, '__diamondSearch', {
    value: {
      fetchDiamonds,
      fetchMockDiamonds, // legacy alias
      searchToParams,
      paramsToSearch,
      buildApiParams,
      normaliseRow,
      resolveEndpoint,
    },
    enumerable: false,
    configurable: true,
  });
}
